import { ArrowDownTrayIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  getActiveColumns,
  getProfileDataForExport,
  getProfileDataForExportAll
} from '../../service';
import { exportAllData, exportData } from '../../utils/utils';
import Button from './button';
import { Collapsible } from './collapsible';
import Loading from './loading';

const ExportPopup = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();

  const [columns, setColumns] = useState([]);
  const [expandedColumns, setExpandedColumns] = useState({});
  const [checkedItems, setCheckedItems] = useState({});
  const [checkedGroups, setCheckedGroups] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [exportingColumns, setExportingColumns] = useState({});
  const [exportingGroups, setExportingGroups] = useState({});
  const [exportingSelectedColumns, setExportingSelectedColumns] = useState(false);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      abortControllerRef.current = new AbortController();
      (async () => {
        setIsLoading(true);
        try {
          const { data: columnsList } = await getActiveColumns();
          setColumns(columnsList);

          // Initialize checked state for columns
          const initialCheckedState = columnsList.reduce((acc, column) => {
            acc[column._id] = true;
            return acc;
          }, {});
          setCheckedItems(initialCheckedState);

          // Initialize checked state for groups
          const initialGroupsState = columnsList.reduce((acc, column) => {
            column.groups.forEach((group) => {
              acc[group._id] = true;
            });
            return acc;
          }, {});
          setCheckedGroups(initialGroupsState);

          // Initialize expanded state - set first column to expanded
          const initialExpandedState = columnsList.reduce((acc, column, index) => {
            acc[column._id] = index === 0; // Only expand the first column
            return acc;
          }, {});
          setExpandedColumns(initialExpandedState);
        } catch (e) {
          toast.error('Failed to get columns.');
          console.error('Failed to get columns:', e);
        } finally {
          setIsLoading(false);
          setExportingSelectedColumns(false);
        }
      })();
    } else {
      cancelOngoingRequests();
    }

    return () => {
      cancelOngoingRequests();
    };
  }, [isOpen]);

  const cancelOngoingRequests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const handleColumnExport = useCallback(
    async (columnName, columnId) => {
      if (exportingColumns[columnId]) return;

      setExportingColumns((prev) => ({ ...prev, [columnId]: 'loading' }));
      try {
        const { data: exportList } = await getProfileDataForExport(
          'groupColumnId',
          columnId,
          i18n.language,
          abortControllerRef.current.signal
        );

        const formattedData = {
          headers: [{ key: 'email', name: 'Email' }, ...exportList.headers],
          data: exportList.data,
          fileName: columnName,
          type: 'column',
          columnId: columnId
        };

        try {
          exportData(
            formattedData.headers,
            formattedData.data,
            formattedData.fileName,
            t,
            i18n.language
          );

          setExportingColumns((prev) => ({ ...prev, [columnId]: 'success' }));
          setTimeout(() => {
            setExportingColumns((prev) => ({ ...prev, [columnId]: null }));
          }, 3000);
        } catch (exportError) {
          setExportingColumns((prev) => ({ ...prev, [columnId]: 'failed' }));
          setTimeout(() => {
            setExportingColumns((prev) => ({ ...prev, [columnId]: null }));
          }, 3000);
        }
      } catch (e) {
        if (e.name === 'CanceledError') {
          setExportingColumns((prev) => ({ ...prev, [columnId]: null }));
        } else {
          setExportingColumns((prev) => ({ ...prev, [columnId]: 'failed' }));
          setTimeout(() => {
            setExportingColumns((prev) => ({ ...prev, [columnId]: null }));
          }, 3000);
        }
      }
    },
    [i18n.language, t, exportingColumns]
  );

  const handleGroupExport = useCallback(
    async (groupName, groupId, columnName) => {
      if (exportingGroups[groupId]) return;

      setExportingGroups((prev) => ({ ...prev, [groupId]: 'loading' }));
      try {
        const { data: exportList } = await getProfileDataForExport(
          'groupId',
          groupId,
          i18n.language,
          abortControllerRef.current.signal
        );

        // Ensure proper data structure for the API
        const formattedData = {
          headers: [{ key: 'email', name: 'Email' }, ...exportList.headers],
          data: exportList.data,
          fileName: `${columnName} - ${groupName}`,
          sheetName: groupName.slice(0, 31), // Ensure sheet name doesn't exceed Excel's 31 character limit
          type: 'group',
          groupId: groupId
        };

        try {
          exportData(
            formattedData.headers,
            formattedData.data,
            formattedData.fileName,
            t,
            i18n.language,
            formattedData.sheetName // Pass sheet name to exportData
          );

          setExportingGroups((prev) => ({ ...prev, [groupId]: 'success' }));
          setTimeout(() => {
            setExportingGroups((prev) => ({ ...prev, [groupId]: null }));
          }, 3000);
        } catch (exportError) {
          setExportingGroups((prev) => ({ ...prev, [groupId]: 'failed' }));
          setTimeout(() => {
            setExportingGroups((prev) => ({ ...prev, [groupId]: null }));
          }, 3000);
        }
      } catch (e) {
        if (e.name === 'CanceledError') {
          setExportingGroups((prev) => ({ ...prev, [groupId]: null }));
        } else {
          setExportingGroups((prev) => ({ ...prev, [groupId]: 'failed' }));
          setTimeout(() => {
            setExportingGroups((prev) => ({ ...prev, [groupId]: null }));
          }, 3000);
        }
      }
    },
    [i18n.language, t, exportingGroups]
  );

  if (!isOpen) return null;

  // Check if all groups in a column are checked
  const areAllGroupsChecked = (columnId) => {
    const column = columns.find((col) => col._id === columnId);
    if (!column || !column.groups || column.groups.length === 0) return false;

    return column.groups.every((group) => checkedGroups[group._id]);
  };

  // Check if some but not all groups in a column are checked
  const areSomeGroupsChecked = (columnId) => {
    const column = columns.find((col) => col._id === columnId);
    if (!column || !column.groups || column.groups.length === 0) return false;

    const checkedCount = column.groups.filter((group) => checkedGroups[group._id]).length;
    return checkedCount > 0 && checkedCount < column.groups.length;
  };

  // Check if all columns are checked
  const areAllColumnsChecked = () => {
    if (columns.length === 0) return false;
    return columns.every((column) => {
      if (!column.groups || column.groups.length === 0) return false;
      return column.groups.every((group) => checkedGroups[group._id]);
    });
  };

  // Check if some but not all groups across all columns are checked
  const areSomeGroupsCheckedAcrossColumns = () => {
    if (columns.length === 0) return false;

    // Count total groups and checked groups
    let totalGroups = 0;
    let checkedGroupsCount = 0;

    columns.forEach((column) => {
      if (column.groups && column.groups.length > 0) {
        totalGroups += column.groups.length;
        checkedGroupsCount += column.groups.filter((group) => checkedGroups[group._id]).length;
      }
    });

    return checkedGroupsCount > 0 && checkedGroupsCount < totalGroups;
  };

  const handleColumnCheckboxChange = (columnId) => {
    const newState = !areAllGroupsChecked(columnId);

    // Update column state
    setCheckedItems((prev) => ({
      ...prev,
      [columnId]: newState
    }));

    // Update all groups in this column
    const column = columns.find((col) => col._id === columnId);
    if (column && column.groups) {
      const updatedGroupState = { ...checkedGroups };
      column.groups.forEach((group) => {
        updatedGroupState[group._id] = newState;
      });
      setCheckedGroups(updatedGroupState);
    }
  };

  const handleGroupCheckboxChange = (groupId, columnId) => {
    const newState = !checkedGroups[groupId];

    // Update group state
    setCheckedGroups((prev) => ({
      ...prev,
      [groupId]: newState
    }));

    // Check if all groups in the column are now checked/unchecked and update column state
    const column = columns.find((col) => col._id === columnId);
    if (column && column.groups) {
      const updatedGroupState = {
        ...checkedGroups,
        [groupId]: newState
      };

      const allChecked = column.groups.every((group) =>
        group._id === groupId ? newState : updatedGroupState[group._id]
      );

      setCheckedItems((prev) => ({
        ...prev,
        [columnId]: allChecked
      }));
    }
  };

  const handleSelectAll = () => {
    const newState = !areAllColumnsChecked();

    // Update all columns
    const newColumnState = columns.reduce((acc, column) => {
      acc[column._id] = newState;
      return acc;
    }, {});
    setCheckedItems(newColumnState);

    // Update all groups
    const newGroupState = columns.reduce((acc, column) => {
      column.groups.forEach((group) => {
        acc[group._id] = newState;
      });
      return acc;
    }, {});
    setCheckedGroups(newGroupState);
  };

  const handleExport = async () => {
    if (exportingSelectedColumns) return;

    // Get all checked group IDs
    const checkedGroupIds = Object.entries(checkedGroups)
      .filter(([_, isChecked]) => isChecked)
      .map(([id]) => id);

    if (checkedGroupIds.length === 0) {
      toast.warning(
        t('exportDataPopup.noSelectionWarning', 'Please select at least one group to export')
      );
      return;
    }

    try {
      setExportingSelectedColumns(true);

      // Create a structured selection map that maintains the column-group hierarchy
      const selectionStructure = columns.reduce((acc, column) => {
        // Get selected groups for this column
        const selectedGroupsInColumn = column.groups
          .filter((group) => checkedGroups[group._id])
          .map((group) => group._id);

        // Only include columns that have selected groups
        if (selectedGroupsInColumn.length > 0) {
          acc.push({
            columnId: column._id,
            groups: selectedGroupsInColumn
          });
        }

        return acc;
      }, []);

      const { data: exportItems } = await getProfileDataForExportAll(
        i18n.language,
        { selections: selectionStructure },
        abortControllerRef.current.signal
      );

      exportAllData(exportItems, t, i18n.language);
      onClose();
    } catch (error) {
      if (error.name === 'CanceledError') {
        console.log('Request was cancelled');
      } else {
        toast.error('Failed to export data');
        console.error('Failed to export data: ', error);
      }
    } finally {
      setExportingSelectedColumns(false);
    }
  };

  const handleClose = () => {
    cancelOngoingRequests();
    onClose();
  };

  // Custom component for the column header with export button
  const ColumnHeader = ({ columnName, profileType, columnId }) => (
    <div className="flex items-center justify-between w-full">
      <div>
        <span className="font-medium">{columnName}</span>
        {profileType && <span className="ml-2 text-sm text-gray-500">({profileType.name})</span>}
      </div>
      <div className="ml-auto">
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!exportingColumns[columnId]) {
              handleColumnExport(columnName, columnId);
            }
          }}
          disabled={exportingColumns[columnId] === 'loading'}
          className={clsx(
            'inline-flex items-center justify-center rounded-md py-1 px-3 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-75 h-8 min-w-[90px]',
            exportingColumns[columnId] === 'loading' && 'opacity-75 cursor-not-allowed',
            exportingColumns[columnId] === 'success' && 'border-green-600',
            exportingColumns[columnId] === 'failed' && 'border-red-600'
          )}>
          {exportingColumns[columnId] === 'loading' ? (
            <>
              <Loading loading={true} size="small" className="mr-1" />
              <span>{t('exportDataPopup.exportingLabel', 'Exporting...')}</span>
            </>
          ) : exportingColumns[columnId] === 'success' ? (
            <>
              <CheckCircleIcon className="h-4 w-4 mr-1 text-green-600" />
              <span className="text-green-600">
                {t('exportDataPopup.exportedLabel', 'Exported')}
              </span>
            </>
          ) : exportingColumns[columnId] === 'failed' ? (
            <>
              <XCircleIcon className="h-4 w-4 mr-1 text-red-600" />
              <span className="text-red-600">{t('exportDataPopup.failedLabel', 'Failed')}</span>
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              {t('exportDataPopup.columnExportBtn')}
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Custom checkbox component with indeterminate state
  const SelectAllCheckbox = () => {
    const checkboxRef = useRef(null);
    const isAllChecked = areAllColumnsChecked();
    const isSomeChecked = areSomeGroupsCheckedAcrossColumns();

    useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = !isAllChecked && isSomeChecked;
      }
    }, [isAllChecked, isSomeChecked]);

    return (
      <div className="mb-6 bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-0.5">
            <div className="relative flex items-center justify-center w-5 h-5">
              <input
                ref={checkboxRef}
                type="checkbox"
                checked={isAllChecked}
                onChange={handleSelectAll}
                className={clsx(
                  'form-checkbox w-5 h-5 border-gray-300 rounded transition duration-150 ease-in-out focus:ring-gray-500',
                  !isAllChecked && isSomeChecked && 'opacity-0'
                )}
                style={{ accentColor: '#1f2937', color: '#1f2937' }}
              />
              {/* Custom indeterminate indicator */}
              {!isAllChecked && isSomeChecked && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white border border-gray-300 rounded">
                  <div className="w-2.5 h-0.5 bg-gray-800 rounded-sm"></div>
                </div>
              )}
            </div>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-medium text-gray-800">
              {t('exportDataPopup.selectAllCheckboxLabel')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t(
                'exportDataPopup.selectAllDescription',
                'Select all groups across all columns for export'
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Export button component for group items
  const GroupExportButton = ({ groupName, groupId, columnName }) => {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!exportingGroups[groupId]) {
            handleGroupExport(groupName, groupId, columnName);
          }
        }}
        disabled={exportingGroups[groupId] === 'loading'}
        className={clsx(
          'inline-flex items-center justify-center rounded-md py-1 px-3 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-75 h-8 min-w-[90px]',
          exportingGroups[groupId] === 'loading' && 'opacity-75 cursor-not-allowed',
          exportingGroups[groupId] === 'success' && 'border-green-600',
          exportingGroups[groupId] === 'failed' && 'border-red-600'
        )}>
        {exportingGroups[groupId] === 'loading' ? (
          <>
            <Loading loading={true} size="small" className="mr-1" />
            <span>{t('exportDataPopup.exportingLabel', 'Exporting...')}</span>
          </>
        ) : exportingGroups[groupId] === 'success' ? (
          <>
            <CheckCircleIcon className="h-4 w-4 mr-1 text-green-600" />
            <span className="text-green-600">{t('exportDataPopup.exportedLabel', 'Exported')}</span>
          </>
        ) : exportingGroups[groupId] === 'failed' ? (
          <>
            <XCircleIcon className="h-4 w-4 mr-1 text-red-600" />
            <span className="text-red-600">{t('exportDataPopup.failedLabel', 'Failed')}</span>
          </>
        ) : (
          <>
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
            {t('exportDataPopup.columnExportBtn', 'Export')}
          </>
        )}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t(`exportDataPopup.heading`)}</h2>
          <p className="text-gray-600">{t(`exportDataPopup.subheading`)}</p>
        </div>
        <div className="flex-1 overflow-y-auto p-6 max-h-[650px] bg-gray-50">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Loading loading={true} />
            </div>
          ) : (
            <>
              <SelectAllCheckbox />
              <ul className="space-y-4">
                {columns.map(({ name: columnName, _id: columnId, profileType, groups }) => (
                  <li
                    key={columnId}
                    className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
                    <Collapsible
                      isOpen={expandedColumns[columnId]}
                      onToggle={() =>
                        setExpandedColumns((prev) => ({
                          ...prev,
                          [columnId]: !prev[columnId]
                        }))
                      }
                      checkboxLabel={
                        <ColumnHeader
                          columnName={columnName}
                          profileType={profileType}
                          columnId={columnId}
                        />
                      }
                      isChecked={areAllGroupsChecked(columnId)}
                      isIndeterminate={areSomeGroupsChecked(columnId)}
                      onCheckboxChange={() => handleColumnCheckboxChange(columnId)}
                      level={0}
                      className="w-full">
                      {groups && groups.length > 0 && (
                        <ul className="py-3 space-y-2 pl-8">
                          {groups.map(({ name: groupName, _id: groupId }) => (
                            <li key={groupId} className="px-4">
                              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                                <label className="flex items-center cursor-pointer">
                                  <div className="relative flex items-center justify-center w-4 h-4">
                                    <input
                                      type="checkbox"
                                      checked={checkedGroups[groupId] || false}
                                      onChange={() => handleGroupCheckboxChange(groupId, columnId)}
                                      className="form-checkbox w-4 h-4 border-gray-300 rounded transition duration-150 ease-in-out focus:ring-gray-500"
                                      style={{ accentColor: '#1f2937', color: '#1f2937' }}
                                    />
                                  </div>
                                  <span className="ml-3 text-gray-700">{groupName}</span>
                                </label>
                                <GroupExportButton
                                  groupName={groupName}
                                  groupId={groupId}
                                  columnName={columnName}
                                />
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Collapsible>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
          <Button.ButtonOutlined onClick={handleClose} className="px-6 py-2.5">
            {t('exportDataPopup.cancelBtn')}
          </Button.ButtonOutlined>
          <Button.ButtonFilled
            onClick={handleExport}
            disabled={exportingSelectedColumns}
            className={clsx(
              'px-6 py-2.5',
              exportingSelectedColumns && 'opacity-75 cursor-not-allowed'
            )}>
            {exportingSelectedColumns ? (
              <>
                <Loading loading={true} size="small" className="mr-2" />
                <span>{t('exportDataPopup.exportingLabel', 'Exporting...')}</span>
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                <span>{t('exportDataPopup.exportSelectedBtn')}</span>
              </>
            )}
          </Button.ButtonFilled>
        </div>
      </div>
    </div>
  );
};

export default ExportPopup;
